import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import {
  Box, Button, Chip, Dialog, DialogContent, DialogTitle, Divider, IconButton,
  Stack, TextField, Tooltip, Typography, useMediaQuery, useTheme,
} from "@mui/material";
import { Close, Refresh, Delete, Save, Wifi, WifiOff, PlayArrow, CleaningServices, Sync, History, CameraAlt, BookmarkAdd } from "@mui/icons-material";
import config from "../../Util/config";
import { DeviceSession } from "../../Model/DeviceSession";
import { useAppSelector } from "../../store/hooks";
import { formatDate } from "../../Util/timeConverter";

type LogItem = {
  id: number;
  displayName?: string;
  eventType?: string;
  severity?: string;
  message?: string;
  timestamp?: string;
};

type CommandItem = {
  id: number;
  command: string;
  params?: string;
  createdAt: string;
  executedAt?: string;
};

type Props = {
  device: DeviceSession | null;
  open: boolean;
  onClose: () => void;
  onForceUpdate: (device: DeviceSession) => void;
  onDelete: (device: DeviceSession) => void;
  onRename: (id: number, name: string) => void;
};

const REFRESH_MS = 10000;
const LOG_LIMIT = 300;

function formatUptime(seconds?: number): string {
  if (seconds == null) return "—";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function severityColor(sev?: string): string {
  switch ((sev || "").toUpperCase()) {
    case "ERROR": return "#ef4444";
    case "WARNING": return "#f59e0b";
    case "SUCCESS": return "#22c55e";
    default: return "#94a3b8";
  }
}

const InfoCell = ({ label, value }: { label: string; value: ReactNode }) => (
  <Box sx={{ minWidth: 120 }}>
    <Typography sx={{ fontSize: 11, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.4 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{value ?? "—"}</Typography>
  </Box>
);

const COMMANDS = [
  { key: "RELOAD_PLAYLIST", label: "Recarregar Playlist", icon: <PlayArrow fontSize="small" />, color: "primary" as const },
  { key: "CLEAR_CACHE",    label: "Limpar Cache",        icon: <CleaningServices fontSize="small" />, color: "warning" as const },
  { key: "SYNC",           label: "Sincronizar",          icon: <Sync fontSize="small" />,           color: "secondary" as const },
];

const DeviceDetailsModal = ({ device, open, onClose, onForceUpdate, onDelete, onRename }: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [logs, setLogs] = useState<LogItem[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [commands, setCommands] = useState<CommandItem[]>([]);
  const [sendingCommand, setSendingCommand] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const token = useAppSelector((state) => state.auth.token);

  const deviceCode = device?.deviceCode;

  const fetchLogs = useCallback(async () => {
    if (!deviceCode || !token) { setLogs([]); return; }
    try {
      setLoadingLogs(true);
      const res = await fetch(
        `${config.API.BASE_URL}/display-log/recent?limit=${LOG_LIMIT}&displayName=${encodeURIComponent(deviceCode)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) return;
      const data: LogItem[] = await res.json();
      setLogs(data);
    } catch {
      /* silencioso */
    } finally {
      setLoadingLogs(false);
    }
  }, [deviceCode, token]);

  const fetchCommands = useCallback(async () => {
    if (!device?.id || !token) return;
    try {
      const res = await fetch(
        `${config.API.BASE_URL}/pending-command/device/${device.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) return;
      setCommands(await res.json());
    } catch { /* silencioso */ }
  }, [device?.id, token]);

  const [savedShots, setSavedShots] = useState<string[]>([]);
  const [previewShot, setPreviewShot] = useState<string | null>(null);
  const [previewZoom, setPreviewZoom] = useState(false);
  const [historyZoom, setHistoryZoom] = useState<string | null>(null);
  const [waitingShot, setWaitingShot] = useState(false);

  const fetchSavedShots = useCallback(async () => {
    if (!deviceCode) return;
    try {
      const res = await fetch(`${config.API.BASE_URL}/device-session/${deviceCode}/screenshots`);
      if (!res.ok) return;
      const list: string[] = await res.json();
      setSavedShots(list);
    } catch { /* silencioso */ }
  }, [deviceCode]);

  const sendCommand = async (command: string) => {
    if (!device?.id || !token) return;
    setSendingCommand(command);
    try {
      const res = await fetch(`${config.API.BASE_URL}/pending-command`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ deviceSessionId: device.id, command, params: null }),
      });
      if (res.ok) {
        fetchCommands();
        if (command === "SCREENSHOT") {
          setWaitingShot(true);
          setPreviewShot(null);
          // polling: aguarda preview aparecer no backend a cada 4s por até 30s
          let attempts = 0;
          const poll = setInterval(async () => {
            attempts++;
            try {
              const r = await fetch(`${config.API.BASE_URL}/device-session/${deviceCode}/screenshots/preview`);
              if (r.ok) {
                const data = await r.json() as { url?: string };
                if (data.url) { setPreviewShot(data.url); clearInterval(poll); setWaitingShot(false); return; }
              }
            } catch { /* silencioso */ }
            if (attempts >= 8) { clearInterval(poll); setWaitingShot(false); }
          }, 4000);
        }
      }
    } catch { /* silencioso */ }
    finally { setSendingCommand(null); }
  };

  useEffect(() => {
    if (!open) return;
    setNameDraft(device?.name ?? "");
    setPreviewShot(null);
    setSavedShots([]);
    fetchLogs();
    fetchCommands();
    fetchSavedShots();
    timerRef.current = setInterval(() => { fetchLogs(); fetchCommands(); }, REFRESH_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [open, device?.name, fetchLogs, fetchCommands, fetchSavedShots]);

  if (!device) return null;

  const canForceUpdate = device.status && !!device.deviceCode;
  const nameChanged = nameDraft.trim() !== "" && nameDraft !== (device.name ?? "");

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1 }}>
        {device.status
          ? <Wifi color="success" fontSize="small" />
          : <WifiOff color="error" fontSize="small" />}
        <Typography sx={{ fontSize: 18, fontWeight: 700, flex: 1 }}>
          {device.name || device.model || "Dispositivo"}
        </Typography>
        <Chip
          label={device.status ? "Online" : "Offline"}
          size="small"
          color={device.status ? "success" : "default"}
        />
        <IconButton onClick={onClose}><Close /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Apelido editável */}
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
          <TextField
            label="Apelido"
            size="small"
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            sx={{ flex: 1, maxWidth: 360 }}
          />
          <Button
            variant="contained"
            startIcon={<Save />}
            disabled={!nameChanged}
            onClick={() => onRename(device.id, nameDraft.trim())}
          >
            Salvar
          </Button>
        </Stack>

        {/* Grid de informações */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5, mb: 2 }}>
          <InfoCell label="Modelo" value={device.model} />
          <InfoCell label="Android" value={device.androidVersion} />
          <InfoCell label="App" value={device.appVersion} />
          <InfoCell label="RAM livre" value={
            device.ramFreeMb != null
              ? device.ramFreeMb > 32768
                ? `${Math.round(device.ramFreeMb / 1024)} MB`
                : `${device.ramFreeMb} MB`
              : "—"
          } />
          <InfoCell label="Ativo há" value={formatUptime(device.uptimeSeconds)} />
          <InfoCell label="Reinícios" value={device.startCount ?? "—"} />
          <InfoCell label="Playlist" value={device.playlist?.name} />
          <InfoCell label="Código" value={device.deviceCode} />
          <InfoCell label="Último sinal" value={formatDate(device.lastSeen)} />
          {device.cpuPercent != null && <InfoCell label="CPU" value={`${device.cpuPercent}%`} />}
          {device.rssiDbm != null && <InfoCell label="WiFi" value={`${device.rssiDbm} dBm`} />}
          {device.diskFreePercent != null && <InfoCell label="Disco livre" value={`${device.diskFreePercent}%`} />}
          {device.temperatureCelsius != null && <InfoCell label="Temperatura" value={`${device.temperatureCelsius.toFixed(1)}°C`} />}
        </Box>

        {/* Ações */}
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Tooltip title={canForceUpdate ? "" : "Só para TVs online com código registrado"}>
            <span>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<Refresh />}
                disabled={!canForceUpdate}
                onClick={() => onForceUpdate(device)}
              >
                Forçar Atualização
              </Button>
            </span>
          </Tooltip>
          <Button variant="outlined" color="error" startIcon={<Delete />} onClick={() => onDelete(device)}>
            Excluir
          </Button>
        </Stack>

        <Divider sx={{ mb: 1.5 }} />

        {/* Comandos remotos */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1 }}>Comandos Remotos</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
            {COMMANDS.map((cmd) => (
              <Button
                key={cmd.key}
                variant="outlined"
                color={cmd.color}
                size="small"
                startIcon={cmd.icon}
                disabled={sendingCommand === cmd.key}
                onClick={() => sendCommand(cmd.key)}
              >
                {sendingCommand === cmd.key ? "Enviando..." : cmd.label}
              </Button>
            ))}
          </Stack>

          {commands.length > 0 && (
            <Box>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.75 }}>
                <History sx={{ fontSize: 14, color: "text.secondary" }} />
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Histórico</Typography>
              </Stack>
              <Box sx={{
                bgcolor: "action.hover", borderRadius: 1.5, p: 1,
                maxHeight: 150, overflowY: "auto",
              }}>
                {commands.slice(0, 20).map((c) => (
                  <Box key={c.id} sx={{ display: "flex", gap: 1.5, alignItems: "center", py: 0.4 }}>
                    <Chip
                      label={c.command}
                      size="small"
                      color={c.executedAt ? "success" : "warning"}
                      sx={{ fontSize: 10, height: 18 }}
                    />
                    <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                      {c.createdAt?.slice(11, 19)}
                    </Typography>
                    {c.executedAt
                      ? <Typography sx={{ fontSize: 11, color: "success.main" }}>✓ {c.executedAt.slice(11, 19)}</Typography>
                      : <Typography sx={{ fontSize: 11, color: "warning.main" }}>pendente</Typography>
                    }
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>

        <Divider sx={{ mb: 1.5 }} />

        {/* Screenshot remoto */}
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Screenshots</Typography>
            {waitingShot && (
              <Typography sx={{ fontSize: 11, color: "warning.main", fontStyle: "italic" }}>
                Aguardando TV...
              </Typography>
            )}
            <Box sx={{ flex: 1 }} />
            <Button
              variant="outlined"
              size="small"
              startIcon={<CameraAlt fontSize="small" />}
              disabled={!device.status || !device.deviceCode || sendingCommand === "SCREENSHOT" || waitingShot}
              onClick={() => sendCommand("SCREENSHOT")}
            >
              {sendingCommand === "SCREENSHOT" ? "Enviando..." : "Tirar Screenshot"}
            </Button>
          </Stack>

          {/* Preview efêmero — aparece apenas após tirar screenshot */}
          {previewShot && (
            <Box sx={{
              border: "1px solid", borderColor: "primary.main", borderRadius: 2,
              overflow: "hidden", mb: 1.5,
            }}>
              <Box
                component="img"
                src={previewShot}
                alt="Preview do screenshot"
                onClick={() => setPreviewZoom(true)}
                sx={{
                  width: "100%", maxHeight: 260, objectFit: "contain",
                  bgcolor: "#000", cursor: "zoom-in", display: "block",
                  transition: "opacity 0.15s", "&:hover": { opacity: 0.85 },
                }}
                onError={() => {}}
              />
              <Stack direction="row" spacing={1} sx={{ p: 1, bgcolor: "action.hover" }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<BookmarkAdd fontSize="small" />}
                  onClick={async () => {
                    if (!previewShot || !deviceCode || !token) return;
                    const filename = previewShot.split("/").pop() ?? "";
                    try {
                      const res = await fetch(
                        `${config.API.BASE_URL}/device-session/${deviceCode}/screenshots/save?filename=${encodeURIComponent(filename)}`,
                        { method: "POST", headers: { Authorization: `Bearer ${token}` } }
                      );
                      if (res.ok) { setPreviewShot(null); fetchSavedShots(); }
                    } catch { /* silencioso */ }
                  }}
                >
                  Salvar
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setPreviewShot(null)}
                >
                  Descartar
                </Button>
              </Stack>
            </Box>
          )}

          {/* Histórico de screenshots salvos */}
          {savedShots.length > 0 && (
            <Box>
              <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.75 }}>
                Histórico salvo ({savedShots.length})
              </Typography>
              <Stack direction="row" spacing={0.75} sx={{ overflowX: "auto", pb: 0.5 }}>
                {savedShots.map((url, i) => (
                  <Box
                    key={url}
                    component="img"
                    src={url}
                    alt={`Screenshot ${i + 1}`}
                    onClick={() => setHistoryZoom(url)}
                    sx={{
                      width: 96, height: 60, objectFit: "cover", flexShrink: 0,
                      borderRadius: 1, cursor: "pointer",
                      border: "2px solid transparent",
                      transition: "all 0.15s",
                      "&:hover": { borderColor: "primary.main", opacity: 0.9 },
                    }}
                    onError={() => {}}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {!previewShot && savedShots.length === 0 && !waitingShot && (
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              Clique em "Tirar Screenshot" para capturar a tela da TV.
            </Typography>
          )}

          {/* Fullscreen preview */}
          <Dialog open={previewZoom} onClose={() => setPreviewZoom(false)} maxWidth="xl">
            <Box sx={{ position: "relative", bgcolor: "#000" }}>
              <IconButton
                onClick={() => setPreviewZoom(false)}
                sx={{ position: "absolute", top: 8, right: 8, bgcolor: "rgba(0,0,0,0.5)", color: "#fff", zIndex: 1 }}
              >
                <Close />
              </IconButton>
              {previewShot && (
                <Box
                  component="img"
                  src={previewShot}
                  alt="Screenshot ampliado"
                  sx={{ display: "block", maxWidth: "90vw", maxHeight: "90vh", objectFit: "contain" }}
                />
              )}
            </Box>
          </Dialog>

          {/* Fullscreen histórico */}
          <Dialog open={!!historyZoom} onClose={() => setHistoryZoom(null)} maxWidth="xl">
            <Box sx={{ position: "relative", bgcolor: "#000" }}>
              <IconButton
                onClick={() => setHistoryZoom(null)}
                sx={{ position: "absolute", top: 8, right: 8, bgcolor: "rgba(0,0,0,0.5)", color: "#fff", zIndex: 1 }}
              >
                <Close />
              </IconButton>
              {historyZoom && (
                <Box
                  component="img"
                  src={historyZoom}
                  alt="Screenshot histórico ampliado"
                  sx={{ display: "block", maxWidth: "90vw", maxHeight: "90vh", objectFit: "contain" }}
                />
              )}
            </Box>
          </Dialog>
        </Box>

        <Divider sx={{ mb: 1 }} />

        {/* Logs ao vivo */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Logs ao vivo</Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            (atualiza a cada {REFRESH_MS / 1000}s)
          </Typography>
          <Box sx={{ flex: 1 }} />
          <IconButton size="small" onClick={fetchLogs} disabled={loadingLogs}>
            <Refresh fontSize="small" />
          </IconButton>
        </Box>

        <Box
          sx={{
            bgcolor: "#0b1220", color: "#cbd5e1", borderRadius: 2, p: 1.5,
            height: 300, overflowY: "auto", fontFamily: "monospace", fontSize: 12.5,
          }}
        >
          {!deviceCode && (
            <Typography sx={{ color: "#f59e0b", fontFamily: "monospace", fontSize: 12.5 }}>
              Esta TV ainda não registrou um código (sem logs).
            </Typography>
          )}
          {deviceCode && logs.length === 0 && (
            <Typography sx={{ color: "#94a3b8", fontFamily: "monospace", fontSize: 12.5 }}>
              Sem logs recentes desta TV.
            </Typography>
          )}
          {logs.map((l) => (
            <Box key={l.id} sx={{ whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
              <span style={{ color: "#64748b" }}>{l.timestamp?.slice(11, 19)} </span>
              <span style={{ color: severityColor(l.severity), fontWeight: 700 }}>
                [{l.eventType}]
              </span>{" "}
              <span>{l.message}</span>
            </Box>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default DeviceDetailsModal;
